import { MultiSelect, TextInput } from "@mantine/core"
import { Checkbox, Icon, Tooltip } from "@shopify/polaris"
import { CircleInformationMajor } from "@shopify/polaris-icons"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { countryCodes } from "../../utils/countryCodes.js"
import { IPv4Regex, IPv6Regex } from "../../utils/ipRegex"
import { GeolocationRedirectEntryPreview } from "./GeolocationRedirectEntryPreview.jsx"

const COUNTRY_CODES = Object.keys(countryCodes)
const COUNTRIES_VALUES = COUNTRY_CODES.map(
	(countryCode) => COUNTRY_CODES[countryCode],
)

const GeolocationRedirectEntryForm = ({
	isEditingRedirectEntry,
	redirectEntryToEdit,
	redirectEntryToEditIndex,
	onSaveChanges,
	onClose,
}) => {
	const [t] = useTranslation()
	const [title, setTitle] = useState(redirectEntryToEdit?.title || "")
	const [countriesAndIpsToRedirect, setCountriesAndIpsToRedirect] = useState(
		redirectEntryToEdit?.countriesAndIpsToRedirect || [],
	)
	const [redirectTo, setRedirectTo] = useState(
		redirectEntryToEdit?.redirectTo || "",
	)
	const [maintainPath, setMaintainPath] = useState(
		redirectEntryToEdit?.maintainPath || false,
	)

	const [userIsTypingIpAddress, setUserIsTypingIpAddress] = useState(false)
	const [ipError, setIpError] = useState(null)
	const [redirectToErrorMessage, setRedirectToErrorMessage] = useState(null)

	const addIp = (ipAddress) => {
		if (!IPv4Regex.test(ipAddress) && !IPv6Regex.test(ipAddress)) {
			setIpError(
				"Incorrect IP address format. Enter a valid IPv4 or IPv6 address.",
			)
		} else if (countriesAndIpsToRedirect.includes(ipAddress)) {
			setIpError("That IP address is already in the whitelist.")
		} else {
			setIpError(null)
			setCountriesAndIpsToRedirect([
				...countriesAndIpsToRedirect,
				ipAddress,
			])
		}
	}

	const changeRedirectTo = (newRedirectTo) => {
		setRedirectTo(newRedirectTo)

		if (!newRedirectTo.startsWith("https://")) {
			setRedirectToErrorMessage("Invalid URL. Must contain https://")
		} else {
			setRedirectToErrorMessage(null)
		}
	}

	const saveChanges = () => {
		onSaveChanges({
			...redirectEntryToEdit,
			title,
			countriesAndIpsToRedirect,
			redirectTo: redirectTo.replace(/\/+$/, ""), // Remove trailing slash
			maintainPath,
		})

		setTitle("")
		setCountriesAndIpsToRedirect([])
		setRedirectTo("")
		setMaintainPath(false)
	}

	const titleError =
		title.length === 0 && t("protect.geolocation_redirect.title_error")
	const countriesAndIpsError =
		countriesAndIpsToRedirect.length === 0 &&
		t("protect.geolocation_redirect.countries_and_ips_error")
	const redirectToError =
		redirectTo.length === 0
			? t("protect.geolocation_redirect.redirect_to_error")
			: redirectToErrorMessage

	return (
		<div style={{ marginBottom: "1rem", position: "relative" }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					marginBottom: "1rem",
				}}
			>
				<h2 style={{ fontSize: "1.2rem", fontWeight: "600" }}>
					{t("protect.geolocation_redirect.new_redirect_entry")}
				</h2>
			</div>

			<div style={{ display: "flex", gap: "2rem", marginBottom: "1rem" }}>
				<div style={{ width: "40%" }}>
					<TextInput
						label={t("protect.geolocation_redirect.new_title")}
						placeholder={t(
							"protect.geolocation_redirect.new_title",
						)}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						error={titleError}
						variant="filled"
						radius="lg"
					/>
				</div>

				<div style={{ width: "60%" }}>
					<MultiSelect
						label={t(
							"protect.geolocation_redirect.countries_and_ips_redirect",
						)}
						placeholder={t(
							"protect.geolocation_redirect.start_typing_a_country",
						)}
						data={[
							...COUNTRY_CODES.map((country, i) => {
								return {
									label: countryCodes[country],
									value: `${country}`,
								}
							}),
							...countriesAndIpsToRedirect,
						]}
						value={countriesAndIpsToRedirect}
						creatable={userIsTypingIpAddress}
						searchable
						onChange={(change) => {
							setCountriesAndIpsToRedirect(change)
						}}
						onCreate={(e) => addIp(e)}
						onKeyDown={(e) => {
							const value = e.target.value

							if (userIsTypingIpAddress && e.key === "Enter") {
								addIp(value)
							}

							if (/\d/.test(value)) {
								setUserIsTypingIpAddress(true)
							} else {
								setUserIsTypingIpAddress(false)
							}
						}}
						getCreateLabel={(query) => `+ Add ${query}`}
						radius="lg"
						error={ipError || countriesAndIpsError}
					/>
				</div>
			</div>

			<div style={{ marginBottom: "1rem" }}>
				<TextInput
					label={t("protect.geolocation_redirect.redirect_to")}
					placeholder="https://www.mystore.co.uk"
					error={redirectToError}
					variant="filled"
					radius="lg"
					value={redirectTo}
					onChange={(e) => changeRedirectTo(e.target.value)}
				/>
			</div>

			<div
				style={{
					marginBottom: "1rem",
					display: "flex",
					gap: "0.5rem",
					alignItems: "center",
				}}
			>
				<Checkbox
					label={t(
						"protect.geolocation_redirect.maintain_store_path",
					)}
					checked={maintainPath}
					onChange={(value) => setMaintainPath(value)}
				/>
				<Tooltip
					content={
						<div style={{ padding: "0.3rem 0.5rem" }}>
							{t(
								"protect.geolocation_redirect.enabling_this_will_redirect",
							)}
						</div>
					}
				>
					<div style={{ height: "15px", fill: "#777" }}>
						<Icon source={CircleInformationMajor} />
					</div>
				</Tooltip>
			</div>

			<GeolocationRedirectEntryPreview
				countriesAndIpsToRedirect={countriesAndIpsToRedirect}
				redirectTo={redirectTo}
				maintainPath={maintainPath}
			/>

			<div
				style={{
					display: "flex",
					gap: "0.5rem",
					marginLeft: "auto",
					marginTop: "1rem",
					width: "fit-content",
				}}
			>
				<button
					className="neu-background shadow neu-button"
					style={{ padding: "0.6rem 1.2rem" }}
					onClick={onClose}
					type="button"
				>
					<span style={{ fontWeight: "600" }}>
						{t("common.cancel")}
					</span>
				</button>
				<button
					className="neu-background shadow neu-button"
					style={{ padding: "0.6rem 1.2rem" }}
					onClick={saveChanges}
					type="button"
					disabled={
						titleError || countriesAndIpsError || redirectToError
					}
				>
					<span style={{ fontWeight: "600" }}>
						{t("protect.geolocation_redirect.save_redirect_entry")}
					</span>
				</button>
			</div>
		</div>
	)
}

export default GeolocationRedirectEntryForm
